import { Box, Paper, Radio } from "@mui/material";
import { useState, useEffect, useMemo, ChangeEvent } from "react";
import { ErrorComponent } from "../../../../components/error/error.component";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import { PaginatedResult } from "../../../../models/default/paginated-result";
import { Customer } from "../../../../models/general/customer";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import { Forecast, ForecastType } from "../../../../models/general/forecast";
import { formatter } from "../../../../utils/formatter-utils";

interface AnalysisForecastPageComponentProps {
    handleSelectForecast: (forecastId: number | null, forecastDescription: string | null) => void;
    selectedForecast: { id: number | null; name: string | null };
    selectedCustomer: string | null;
}

const AnalysisForecastPageComponent = ({ handleSelectForecast, selectedForecast, selectedCustomer }: AnalysisForecastPageComponentProps) => {
    const service = new BaseService<Customer>(URLS.FORECAST);
    const [dataSource, setDataSource] = useState<Customer[]>([]);
    const [pagination, setPagination] = useState<PaginatedResult<Customer>>();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [selectedForecast]);

    useEffect(() => {
        fetchCustomers();
    }, [page, rowsPerPage]);

    const fetchCustomers = () => {
        if (!selectedCustomer) return;

        service.clearParameters();
        service.addParameter("customer_name", selectedCustomer);
        service.addParameter("type", ForecastType.FLEX_DSI);
        service.addParameter("limit", rowsPerPage);
        service.addParameter("offset", page * rowsPerPage);
        service
            .getAllPaginate()
            .then((response: any) => {
                setPagination(response.data);
                setDataSource(response.data.results);
                if (selectedForecast.id && !response.data.results.some((item: Forecast) => item.id === selectedForecast.id)) {
                    handleSelectForecast(null, null);
                }
            })
            .catch(ErrorComponent);
    };

    const handleSelectCustomersForecast = (forecastId: number, forecastDescription: string) => {
        handleSelectForecast(forecastId, forecastDescription);
    };

    const renderRadioButton = (forecast: Forecast) => (
        <Box sx={{ display: "flex", alignItems: "center" }} key={forecast.id} onClick={() => handleSelectCustomersForecast(forecast.id, forecast?.description ?? "")}>
            <Radio
                color="primary"
                id={`radio-forecast-${forecast.id}`}
                checked={selectedForecast.id === forecast.id}
                onChange={() => handleSelectCustomersForecast(forecast.id, forecast?.description ?? "")}
            />
            {formatter.formatDateTime(forecast.created_at)}
        </Box>
    );

    const handleRowClick = (row: any) => {
        handleSelectCustomersForecast(row.id, row?.description ?? "");
    };

    const mappedDataSource = useMemo(() =>
        dataSource.map((forecast) => ({
            ...forecast,
            created_at: renderRadioButton(forecast),
        }))
    , [dataSource, selectedForecast]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderCellHandler = (column: string, index: number, row: any) => {
        return column === "created_at" ? row.created_at : row[column];
    };

    const headerCellCustomStyleHandler = (column: any) => {
        const baseStyle = {
            backgroundColor: "#FFFF !important",
            color: "var(--primary-main) !important",
            borderBottom: "1px solid var(--primary-main)",
            borderTop: "1px solid var(--primary-main)",
        };

        return column === "creation_date"
            ? { ...baseStyle, paddingLeft: "14px" }
            : baseStyle;
    };

    return (
        <section>
            <Paper variant="outlined" sx={{ marginTop: "16px" }}>
                <TablePaginationComponent
                    id="table-analysis-customer"
                    state={mappedDataSource}
                    displayedColumnsShow={["creation_date", "description", "customer"]}
                    displayedColumns={["created_at", "description", "customer_name"]}
                    columnStyles={{ 1: "150px", 2: "auto" }}
                    dateColumns={["creation_date"]}
                    renderCellHandler={{ created_at: renderCellHandler }}
                    headerCellCustomStyleHandler={headerCellCustomStyleHandler}
                    handleRowClick={handleRowClick}
                    count={pagination?.count}
                    next={pagination?.next}
                    previous={pagination?.previous}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
        </section>
    );
};

export default AnalysisForecastPageComponent;
